import {
  BillingBusinessLicense,
  BillingLicensePlans,
} from '@kizen/api/billing';
import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { CSSProperties, forwardRef, ReactNode, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PlanCard } from './PlanCard';
import { arePlansEqual, getUnifiedPlan, UnifiedPlan } from '../util';
import useModal from '__components/Modals/useModal';
import useEndsOfScroll from 'hooks/useEndsOfScroll';
import { WarningModal } from './WarningModal';
import { CurrentlyActivePlan } from '../hooks/useSelectedPlanResolution';

const MAX_CARDS_BEFORE_SCROLL = 4;

type PlanRowProps = {
  plans: BillingLicensePlans['license_templates'];
  title: string;
  period: string;
  unit?: string;
  currentPlan?: BillingBusinessLicense[number];
  selectedPlanIndex: number | null;
  setSelectedPlanIndex?: (index: number, plan: UnifiedPlan | undefined) => void;
  numberInput?: boolean;
  numberInputLabel?: string;
  numberInputValue?: number;
  onNumberInputValueChange?: (value: number) => void;
  numberInputDisabled?: boolean;
  selectable?: boolean;
  scrollToGeneralPlanRow?: () => void;
  pendingDowngradePlan?: CurrentlyActivePlan;
  onCancelDowngrade: (id: string) => void;
  downgradeDate?: string;
  freePlanSelectable?: boolean;
  showAnnualPricing?: boolean;
  isCorePlan?: boolean;
  titleAccessory?: ReactNode;
};

export const PlanRow = forwardRef<HTMLDivElement, PlanRowProps>(
  (props, ref) => {
    const {
      plans,
      title,
      period,
      unit,
      currentPlan,
      selectedPlanIndex: selectedPlanIndexProp,
      setSelectedPlanIndex,
      numberInput,
      numberInputLabel,
      numberInputValue,
      onNumberInputValueChange,
      numberInputDisabled,
      selectable = true,
      scrollToGeneralPlanRow,
      pendingDowngradePlan,
      onCancelDowngrade,
      downgradeDate,
      freePlanSelectable = true,
      showAnnualPricing = false,
      isCorePlan = false,
      titleAccessory,
    } = props;

    // TODO: Fix this type once the schemas are correct
    const pendingDowngradeMaxUsers = (
      pendingDowngradePlan?.config?.limits as any
    )?.max_users;

    const [warningModalProps, , warningModal] = useModal({
      handleSubmit: () => {
        scrollToGeneralPlanRow?.();
      },
      handleCancel: () => null,
    });

    const [warningModalDowngradeProps, , warningModalDowngrade] = useModal({
      handleSubmit: () => {
        scrollToGeneralPlanRow?.();
      },
      handleCancel: () => null,
    });

    const { t } = useTranslation();

    const activePlanIndex = useMemo(() => {
      return plans.findIndex((plan) => {
        return arePlansEqual(plan, currentPlan);
      });
    }, [currentPlan, plans]);

    const downgradePlanIndex = useMemo(() => {
      return plans.findIndex((plan) => {
        return arePlansEqual(plan, pendingDowngradePlan);
      });
    }, [pendingDowngradePlan, plans]);

    const hasDowngradePlan = Boolean(pendingDowngradePlan);
    const hasLegacyDowngradePlan = hasDowngradePlan && downgradePlanIndex < 0;

    const hasLegacyPlan = activePlanIndex < 0;

    const selectedPlanIndex =
      selectedPlanIndexProp === null ? activePlanIndex : selectedPlanIndexProp;

    const cards: ReactNode[] = [];
    const cardKeys: string[] = [];

    if (hasLegacyPlan) {
      cardKeys.push('legacy');
      cards.push(
        <PlanCard
          key="legacy"
          currentPlan={currentPlan}
          isCurrentPlan
          isSelectedPlan={selectedPlanIndex === -1}
          period={period}
          unit={unit}
          isLegacy
          onSelectPlan={() => {
            /*
              The legacy plan is always selectable no matter what,
              since it's guaranteed to be your current plan
            */
            return setSelectedPlanIndex?.(
              -1,
              getUnifiedPlan(undefined, currentPlan)
            );
          }}
          numberInput={numberInput}
          numberInputLabel={numberInputLabel}
          numberInputValue={numberInputValue}
          onNumberInputValueChange={onNumberInputValueChange}
          numberInputDisabled={numberInputDisabled}
          pendingDowngradeMaxUsers={pendingDowngradeMaxUsers}
          showAnnualPricing={showAnnualPricing}
          isCorePlan={isCorePlan}
        />
      );
    }

    if (hasLegacyDowngradePlan) {
      cardKeys.push('legacy-downgrade');
      cards.push(
        <PlanCard
          key="legacy-downgrade"
          currentPlan={currentPlan}
          isDowngradePlan
          period={period}
          unit={unit}
          onCancelDowngrade={() => {
            if (pendingDowngradePlan) {
              return onCancelDowngrade?.((pendingDowngradePlan as any)?.id);
            }
          }}
          numberInput={numberInput}
          numberInputLabel={numberInputLabel}
          numberInputValue={numberInputValue}
          onNumberInputValueChange={onNumberInputValueChange}
          numberInputDisabled={numberInputDisabled}
          downgradeDate={downgradeDate}
          pendingDowngradeMaxUsers={pendingDowngradeMaxUsers}
          showAnnualPricing={showAnnualPricing}
          isCorePlan={isCorePlan}
        />
      );
    }

    plans.forEach((plan, elementIndex) => {
      const isCurrentPlan = elementIndex === activePlanIndex;
      const isDowngradePlan = elementIndex === downgradePlanIndex;
      const isSelectedPlan = elementIndex === selectedPlanIndex;
      const isPaid = plan.price.amount > 0;

      cardKeys.push(plan.id);
      cards.push(
        <PlanCard
          key={plan.id}
          plan={plan}
          period={period}
          unit={unit}
          isCurrentPlan={isCurrentPlan}
          isDowngradePlan={isDowngradePlan}
          isSelectedPlan={isSelectedPlan}
          onSelectPlan={() => {
            /*
              If the plan is not selectable, and it's not free,
              it can't be chosen without a paid core plan, so we show
              a warning
            */
            if (!selectable && !isCurrentPlan && isPaid) {
              return warningModal.show();
            }

            if (!isPaid && !freePlanSelectable) {
              return warningModalDowngrade.show();
            }

            return setSelectedPlanIndex?.(
              elementIndex,
              getUnifiedPlan(plan, undefined)
            );
          }}
          onCancelDowngrade={() => {
            if (pendingDowngradePlan) {
              return onCancelDowngrade?.((pendingDowngradePlan as any)?.id);
            }
          }}
          numberInput={numberInput}
          numberInputLabel={numberInputLabel}
          numberInputValue={numberInputValue}
          onNumberInputValueChange={onNumberInputValueChange}
          numberInputDisabled={numberInputDisabled}
          downgradeDate={downgradeDate}
          pendingDowngradeMaxUsers={pendingDowngradeMaxUsers}
          showAnnualPricing={showAnnualPricing}
          isCorePlan={isCorePlan}
        />
      );
    });

    const cardCount = cards.length;
    const shouldScroll = cardCount > MAX_CARDS_BEFORE_SCROLL;
    const scrollRef = useRef<HTMLDivElement>(null);
    const [, scrolledRight, , scrolledLeft] = useEndsOfScroll(scrollRef, [
      shouldScroll,
      cardCount,
    ]);

    return (
      <div className="flex flex-col" ref={ref}>
        <div className="flex items-center justify-between">
          <div className="flex pb-[5px]">
            <Typography weight="semibold" size="lg">
              {isCorePlan
                ? t('{{title}} - Select Plan', { title })
                : t('{{title}} - Select Additional Plan', { title })}
            </Typography>
          </div>
          <div className="flex pt-[5px]">{titleAccessory}</div>
        </div>
        <div className="relative w-full [container-type:inline-size]">
          {shouldScroll ? (
            <>
              <div
                ref={scrollRef}
                className="overflow-x-auto kds-expanding-scrollbar"
              >
                <div className="flex items-stretch gap-20 pt-[34px]">
                  {cards.map((card, index) => (
                    <div
                      key={cardKeys[index]}
                      className="flex-none w-[calc((100cqw_-_60px)/4)]"
                    >
                      {card}
                    </div>
                  ))}
                </div>
              </div>
              <div
                aria-hidden
                className={`pointer-events-none absolute top-0 bottom-0 left-0 w-[112px] transition-opacity duration-100 bg-[linear-gradient(to_right,var(--color-background-white)_16px,transparent)] ${
                  scrolledLeft ? 'opacity-0' : 'opacity-100'
                }`}
              />
              <div
                aria-hidden
                className={`pointer-events-none absolute top-0 bottom-0 right-0 w-[112px] transition-opacity duration-100 bg-[linear-gradient(to_left,var(--color-background-white)_16px,transparent)] ${
                  scrolledRight ? 'opacity-0' : 'opacity-100'
                }`}
              />
            </>
          ) : (
            <div
              className="grid grid-cols-[repeat(var(--column-count),minmax(0,1fr))] w-full gap-20 items-stretch pt-[34px]"
              style={
                {
                  '--column-count': cardCount,
                } as CSSProperties
              }
            >
              {cards}
            </div>
          )}
        </div>
        <WarningModal modalProps={warningModalProps} planTitle={title} />
        <WarningModal
          corePlanSelection
          modalProps={warningModalDowngradeProps}
          planTitle={title}
        />
      </div>
    );
  }
);
